/**
 * Type-explorer (M8 §4) — read-only overzicht van alle concepten in de
 * ACTIEVE branch. Modal/overlay (geen side-panel) zodat 'ie het volledige
 * scherm mag benutten voor een brede tabel.
 *
 * Data komt volledig uit de al-gehydrateerde workspace-store + de runtime-
 * registry; er is geen apart endpoint. Kolommen:
 *   naam · type · stereotype · laag · # diagrammen · laatst gewijzigd
 *
 * - Chunk 2: data + klik-sorteerbare tabel.
 * - Chunk 3: vrije-tekst-zoek (NL+EN fuzzy) + multi-select filters op
 *   type / laag / stereotype.
 * - Chunk 4: jump-to-usage — klik op de naam opent een popover met de
 *   diagrammen waarop het concept staat; kiezen opent dat diagram en
 *   sluit de modal.
 */
import { fuzzyMatchAny } from "@ae/domain";
import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRegistry, resolveBilingual } from "../registry";
import { typeLabelFor } from "../registry/typeLabel";
import { useDisplayMode } from "../state/displayMode";
import { useSession } from "../state/session";
import { useUi } from "../state/ui";
import { useWorkspaceData } from "../state/workspace";
import { colorForLayer } from "./layerColors";

type SortKey = "name" | "type" | "stereotype" | "layer" | "diagrams" | "lastEdit";
type SortDir = "asc" | "desc";

interface DiagramRef {
  id: string;
  name: string;
}

interface Row {
  id: string;
  name: string;
  nameValues: string[];
  typeId: string;
  typeLabel: string;
  layer: string;
  stereotype: string;
  stereotypeLabels: string[];
  diagramRefs: DiagramRef[];
  updatedAt: string;
}

export function TypeExplorer() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const displayMode = useDisplayMode((s) => s.mode);
  const open = useUi((s) => s.typeExplorerOpen);
  const setOpen = useUi((s) => s.setTypeExplorerOpen);
  const revision = useWorkspaceData((s) => s.revision);
  const openDiagram = useWorkspaceData((s) => s.openDiagram);
  const workspaceId = useSession((s) => s.workspaceId);
  const closeRef = useRef<HTMLButtonElement>(null);

  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const [layerFilter, setLayerFilter] = useState<Set<string>>(new Set());
  const [stereotypeFilter, setStereotypeFilter] = useState<Set<string>>(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  // conceptId waarvan de jump-to-usage-popover open staat (chunk 4).
  const [menuFor, setMenuFor] = useState<string | null>(null);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Escape sluit eerst een open jump-popover, daarna pas de modal.
      if (menuFor) setMenuFor(null);
      else setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, menuFor, setOpen]);

  // Rijen uit de store (actieve branch, via de overlay-resolutie in
  // `listConcepts`). Alleen opgebouwd als de modal open is — bij dicht
  // is het een lege lijst zodat we niet onnodig over 500 concepten lopen.
  const rows = useMemo<Row[]>(() => {
    void revision;
    if (!open) return [];
    const store = useWorkspaceData.getState().store;
    const reg = getRegistry();

    // conceptId → diagrammen waarop 'ie geplaatst is (viewState-scan).
    const usage = new Map<string, DiagramRef[]>();
    for (const d of store.listDiagrams()) {
      const seen = new Set<string>();
      for (const [key, ve] of Object.entries(d.viewState)) {
        if (ve.position === undefined && ve.slotId === undefined) continue;
        seen.add(ve.ref ?? key);
      }
      const dName = resolveBilingual(d.name, lang) || d.id;
      for (const cid of seen) {
        const arr = usage.get(cid) ?? [];
        arr.push({ id: d.id, name: dName });
        usage.set(cid, arr);
      }
    }

    const out: Row[] = [];
    for (const c of store.listConcepts()) {
      if (c.tombstoneInBranch) continue;
      if (c.typeId.startsWith("drawing.")) continue;
      const et = reg.getElementType(c.typeId);
      // Type-kolom volgt de ArchiMate-weergavemodus: in v3.1 toont een
      // gemigreerd concept z'n oude type ("Bedrijfsproces"), in v4 het
      // generieke ("Proces"). De badge laten we hier weg — de
      // stereotype-kolom ernaast toont 'm al.
      const typeLabel = typeLabelFor(c, lang, displayMode).label || c.typeId;
      const layer = et?.layer ?? "other";
      const stereotypeLabels = c.stereotypes.map((sid) => {
        const bundle = reg.getStereotype(sid);
        return bundle ? resolveBilingual(bundle.label, lang) : sid;
      });
      out.push({
        id: c.id,
        name: resolveBilingual(c.name, lang) || c.id,
        nameValues: Object.values(c.name).filter((v): v is string => !!v),
        typeId: c.typeId,
        typeLabel,
        layer,
        stereotype: stereotypeLabels.join(", "),
        stereotypeLabels,
        diagramRefs: usage.get(c.id) ?? [],
        updatedAt: c.updatedAt ?? "",
      });
    }
    return out;
  }, [open, revision, lang, displayMode]);

  // Filter-universum voor de chip-groepen (uit de ONgefilterde set zodat
  // chips niet verdwijnen zodra je de lijst versmalt).
  const universe = useMemo(() => {
    const types = new Set<string>();
    const layers = new Set<string>();
    const stereotypes = new Set<string>();
    for (const r of rows) {
      types.add(r.typeLabel);
      layers.add(r.layer);
      for (const s of r.stereotypeLabels) stereotypes.add(s);
    }
    return {
      types: [...types].sort((a, b) => a.localeCompare(b, lang)),
      layers: [...layers].sort((a, b) => a.localeCompare(b, lang)),
      stereotypes: [...stereotypes].sort((a, b) => a.localeCompare(b, lang)),
    };
  }, [rows, lang]);

  const filtered = useMemo(() => {
    const q = deferredQuery.trim();
    return rows.filter((r) => {
      if (typeFilter.size > 0 && !typeFilter.has(r.typeLabel)) return false;
      if (layerFilter.size > 0 && !layerFilter.has(r.layer)) return false;
      if (stereotypeFilter.size > 0 && !r.stereotypeLabels.some((s) => stereotypeFilter.has(s)))
        return false;
      if (q && !fuzzyMatchAny(r.nameValues, q)) return false;
      return true;
    });
  }, [rows, deferredQuery, typeFilter, layerFilter, stereotypeFilter]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const cmp = (a: Row, b: Row): number => {
      switch (sortKey) {
        case "diagrams":
          return (a.diagramRefs.length - b.diagramRefs.length) * dir;
        case "type":
          return a.typeLabel.localeCompare(b.typeLabel, lang) * dir;
        case "stereotype":
          return a.stereotype.localeCompare(b.stereotype, lang) * dir;
        case "layer":
          return a.layer.localeCompare(b.layer, lang) * dir;
        case "lastEdit":
          return a.updatedAt.localeCompare(b.updatedAt) * dir;
        default:
          return a.name.localeCompare(b.name, lang) * dir;
      }
    };
    return [...filtered].sort(cmp);
  }, [filtered, sortKey, sortDir, lang]);

  if (!open) return null;

  const activeFilterCount = typeFilter.size + layerFilter.size + stereotypeFilter.size;

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "diagrams" || key === "lastEdit" ? "desc" : "asc");
    }
  };

  const arrow = (key: SortKey) => (key === sortKey ? (sortDir === "asc" ? " ▲" : " ▼") : "");

  const th = (key: SortKey, label: string, extra?: string) => (
    <th className={`px-2 py-1 font-medium ${extra ?? ""}`}>
      <button
        type="button"
        className="flex items-center gap-0.5 hover:text-(--ld-text)"
        onClick={() => toggleSort(key)}
        aria-label={label}
      >
        {label}
        {arrow(key)}
      </button>
    </th>
  );

  const jump = (diagramId: string) => {
    if (workspaceId) openDiagram(workspaceId, diagramId);
    setMenuFor(null);
    setOpen(false);
  };

  const clearFilters = () => {
    setTypeFilter(new Set());
    setLayerFilter(new Set());
    setStereotypeFilter(new Set());
    setQuery("");
  };

  return (
    <div
      className="ledger fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="type-explorer-title"
    >
      <div
        className="flex h-full w-full max-w-6xl flex-col rounded-lg border border-(--ld-control) bg-(--ld-surface) shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex shrink-0 items-center justify-between border-(--ld-border) border-b px-4 py-2">
          <h2 id="type-explorer-title" className="font-semibold text-(--ld-text) text-sm">
            {t("typeExplorer.title")}{" "}
            <span className="font-normal text-(--ld-muted)">
              ({t("typeExplorer.count", { count: sorted.length })})
            </span>
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="rounded px-2 py-0.5 text-(--ld-muted) text-sm hover:bg-(--ld-raised) hover:text-(--ld-text)"
            onClick={() => setOpen(false)}
            aria-label={t("typeExplorer.close")}
          >
            ✕
          </button>
        </header>

        {/* Zoek + filter-balk (chunk 3). */}
        <div className="flex shrink-0 flex-col gap-1.5 border-(--ld-border) border-b px-4 py-2">
          <div className="flex items-center gap-2">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("typeExplorer.search")}
              className="w-64 rounded border border-(--ld-control) px-2 py-1 text-xs focus:border-(--ld-accent) focus:outline-none"
            />
            <button
              type="button"
              className={`rounded border px-2 py-1 text-xs ${
                filtersOpen || activeFilterCount > 0
                  ? "border-(--ld-accent) bg-(--ld-active) text-(--ld-accent)"
                  : "border-(--ld-control) bg-(--ld-surface) text-(--ld-muted) hover:bg-(--ld-raised)"
              }`}
              onClick={() => setFiltersOpen((v) => !v)}
            >
              {t("typeExplorer.filters")}
              {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </button>
            {(activeFilterCount > 0 || query) && (
              <button
                type="button"
                className="text-(--ld-muted) text-xs underline hover:text-(--ld-text)"
                onClick={clearFilters}
              >
                {t("typeExplorer.clearFilters")}
              </button>
            )}
          </div>
          {filtersOpen && (
            <div className="flex flex-col gap-1.5 pt-1">
              <FilterGroup
                label={t("typeExplorer.col.type")}
                options={universe.types}
                selected={typeFilter}
                onChange={setTypeFilter}
              />
              <FilterGroup
                label={t("typeExplorer.col.layer")}
                options={universe.layers}
                selected={layerFilter}
                onChange={setLayerFilter}
              />
              <FilterGroup
                label={t("typeExplorer.col.stereotype")}
                options={universe.stereotypes}
                selected={stereotypeFilter}
                onChange={setStereotypeFilter}
                emptyHint={t("typeExplorer.noStereotypes")}
              />
            </div>
          )}
        </div>

        {/* Jump-popover sluit via Escape of door een andere naam aan te
            klikken (die vervangt `menuFor`); geen click-outside-handler op
            de container zodat 'ie a11y-schoon blijft (geen static-element-
            interaction). */}
        <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
          <table className="w-full border-collapse text-xs">
            <thead className="sticky top-0 bg-(--ld-surface)">
              <tr className="border-(--ld-border) border-b text-left text-(--ld-muted)">
                {th("name", t("typeExplorer.col.name"))}
                {th("type", t("typeExplorer.col.type"))}
                {th("stereotype", t("typeExplorer.col.stereotype"))}
                {th("layer", t("typeExplorer.col.layer"))}
                {th("diagrams", t("typeExplorer.col.diagrams"), "text-right")}
                {th("lastEdit", t("typeExplorer.col.lastEdit"))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td className="px-2 py-6 text-center text-(--ld-muted)" colSpan={6}>
                    {t("typeExplorer.empty")}
                  </td>
                </tr>
              ) : (
                sorted.map((r) => (
                  <tr key={r.id} className="border-(--ld-rule) border-b hover:bg-(--ld-raised)">
                    <td className="relative px-2 py-1 text-(--ld-text)">
                      <button
                        type="button"
                        className="text-left hover:text-(--ld-accent) hover:underline"
                        title={t("typeExplorer.jumpHint")}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuFor((cur) => (cur === r.id ? null : r.id));
                        }}
                      >
                        {r.name}
                      </button>
                      {menuFor === r.id && (
                        <div className="absolute left-2 top-full z-10 mt-0.5 w-64 rounded-md border border-(--ld-control) bg-(--ld-surface) py-1 shadow-lg">
                          {r.diagramRefs.length === 0 ? (
                            <div className="px-3 py-1.5 text-(--ld-muted) text-xs italic">
                              {t("typeExplorer.notOnDiagram")}
                            </div>
                          ) : (
                            r.diagramRefs.map((d) => (
                              <button
                                key={d.id}
                                type="button"
                                className="block w-full px-3 py-1.5 text-left text-(--ld-text) text-xs hover:bg-(--ld-raised)"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  jump(d.id);
                                }}
                              >
                                {d.name}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1 text-(--ld-muted)">{r.typeLabel}</td>
                    <td className="px-2 py-1 text-(--ld-muted)">
                      {r.stereotype ? `«${r.stereotype}»` : ""}
                    </td>
                    <td className="px-2 py-1">
                      <span
                        className="inline-block rounded px-1.5 py-0.5 text-xs"
                        style={{
                          backgroundColor: colorForLayer(r.layer).bg,
                          color: colorForLayer(r.layer).text,
                        }}
                      >
                        {r.layer}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-right text-(--ld-muted) tabular-nums">
                      {r.diagramRefs.length}
                    </td>
                    <td className="px-2 py-1 text-(--ld-muted) tabular-nums">
                      {r.updatedAt ? r.updatedAt.slice(0, 16) : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FilterGroup({
  label,
  options,
  selected,
  onChange,
  emptyHint,
}: {
  label: string;
  options: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  emptyHint?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-(--ld-muted)">{label}</span>
      {options.length === 0 ? (
        <span className="text-xs text-(--ld-muted) italic">{emptyHint ?? ""}</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {options.map((opt) => {
            const active = selected.has(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  const next = new Set(selected);
                  if (next.has(opt)) next.delete(opt);
                  else next.add(opt);
                  onChange(next);
                }}
                className={`rounded-full border px-2 py-0.5 text-xs transition ${
                  active
                    ? "border-(--ld-accent) bg-(--ld-active) text-(--ld-accent)"
                    : "border-(--ld-control) bg-(--ld-surface) text-(--ld-muted) hover:bg-(--ld-raised)"
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
